import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import GuideSeo from '../Seo/GuideSeo';
import { Breadcrumbs, Eyebrow } from '../ui';
import { GuideIcon } from '../ui/GuideIcons';
import { getGuideByRoute, guideCategories, localized } from '../../data/guides/guidesRegistry';
import { useProducts, productSlug } from '../../hooks/useProducts';
import './GuideLayout.css';
/**
 * Inhaltsverzeichnis aus den Abschnitten (section.guide-section[id] mit h2) der Seite.
 * Wird bei Änderungen am Inhalt (Sprachwechsel, Anbieterwahl) neu aufgebaut.
 */
function useToc(root) {
    const [items, setItems] = useState([]);
    const [active, setActive] = useState('');
    useEffect(() => {
        const el = root.current;
        if (!el)
            return;
        const read = () => {
            const next = Array.from(el.querySelectorAll('section.guide-section[id]'))
                .map((s) => ({ id: s.id, label: s.querySelector('h2')?.textContent?.trim() || '' }))
                .filter((i) => i.label);
            setItems((prev) => prev.length === next.length && prev.every((p, i) => p.id === next[i].id && p.label === next[i].label) ? prev : next);
        };
        read();
        const mo = new MutationObserver(read);
        mo.observe(el, { childList: true, subtree: true, characterData: true });
        return () => mo.disconnect();
    }, [root]);
    useEffect(() => {
        if (!items.length)
            return;
        const onScroll = () => {
            let current = items[0].id;
            for (const i of items) {
                const s = document.getElementById(i.id);
                if (s && s.getBoundingClientRect().top < 140)
                    current = i.id;
            }
            setActive(current);
        };
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [items]);
    return { items, active };
}
/**
 * Rahmen für alle Anleitungsseiten: SEO, Brotkrumen, Titel, Einleitung und
 * die Liste der Produkte, für die die Anleitung gilt.
 */
const GuideLayout = ({ route, title, lead, children }) => {
    const { t, i18n } = useTranslation();
    const lang = (i18n.language || 'en').split('-')[0];
    const guide = getGuideByRoute(route);
    const category = guideCategories.find((c) => c.id === guide?.category);
    const heading = title || (guide ? localized(guide.title, lang) : '');
    const { products } = useProducts();
    const mainRef = useRef(null);
    const toc = useToc(mainRef);
    const scope = guide?.appliesTo;
    const appliesProducts = Array.isArray(scope) && products.length
        ? products.filter((p) => scope.includes(productSlug(p)))
        : [];
    return (_jsxs("div", { className: "guide-page", children: [_jsx(GuideSeo, { route: route }), _jsxs("div", { className: "guide-container", children: [_jsx(Breadcrumbs, { items: [
                            { label: t('help.title'), to: '/help' },
                            ...(category ? [{ label: localized(category.label, lang), to: `/help#${category.id}` }] : []),
                            { label: heading },
                        ] }), _jsxs("div", { className: "guide-shell", children: [_jsx("aside", { className: "guide-aside", children: toc.items.length > 1 && (_jsxs("nav", { className: "guide-toc", "aria-label": t('g.common.onThisPage'), children: [_jsx(Eyebrow, { as: "p", children: t('g.common.onThisPage') }), _jsx("ol", { children: toc.items.map((i) => (_jsx("li", { children: _jsx("a", { href: `#${i.id}`, className: toc.active === i.id ? 'active' : undefined, children: i.label }) }, i.id))) })] })) }), _jsxs("div", { className: "guide-main", ref: mainRef, children: [_jsxs("header", { className: "guide-header", children: [_jsxs("div", { className: "guide-title-row", children: [guide && (_jsx("span", { className: "icon-tile frame guide-title-icon", children: _jsx(GuideIcon, { id: guide.id, size: 34 }) })), _jsx("h1", { className: "guide-title", children: heading })] }), lead && _jsx("div", { className: "guide-lead", children: lead }), scope === 'watchfaces' && (_jsxs("p", { className: "guide-scope", children: [_jsx(Eyebrow, { children: t('g.common.appliesTo') }), ' ', _jsx("span", { children: t('g.common.allWatchfaces') })] })), appliesProducts.length > 0 && (_jsxs("div", { className: "applies-to guide-scope", children: [_jsx(Eyebrow, { children: t('g.common.appliesTo') }), appliesProducts.map((p) => (_jsx(Link, { to: `/watchface/${productSlug(p)}`, children: p.name }, p.id)))] })), toc.items.length > 1 && (_jsx("nav", { className: "guide-toc-inline", "aria-label": t('g.common.onThisPage'), children: toc.items.map((i) => (_jsx("a", { href: `#${i.id}`, children: i.label }, i.id))) }))] }), _jsx("div", { className: "guide-content", children: children })] })] })] })] }));
};
export default GuideLayout;
/** Ein Abschnitt einer Anleitung (mit optionaler Mono-Beschriftung über der Überschrift). */
export const GuideSection = ({ id, eyebrow, title, children }) => (_jsxs("section", { className: "guide-section", id: id, children: [(eyebrow || title) && (_jsxs("div", { className: "guide-section-head", children: [eyebrow && _jsx(Eyebrow, { children: eyebrow }), title && _jsx("h2", { children: title })] })), children] }));
/** Klappbarer Eintrag für Fehlerbehebung / Fragen innerhalb einer Anleitung. */
export const GuideFaq = ({ id, question, children, }) => (_jsxs("details", { className: "guide-faq", id: id, children: [_jsx("summary", { children: question }), _jsx("div", { className: "guide-faq-body", children: children })] }));
